// frontend/src/lib/domains/auth.ts
// Domain: auth
// - Identity + session live under /xapi/auth/*
// - Personal registration uses the unified /xapi/auth/* endpoints (already working for you)
// - Business registration uses the dedicated /xapi/register/business endpoints (matches your cURL)

import { request } from "../core/request";

/* =======================
 * Types
 * ======================= */

export type Json = Record<string, any>;

export type MeResponse = {
  user: {
    id: number;
    username?: string;
    email: string;
    role?: string;
    admin_role?: string | null;
    customer_type?: "individual" | "business" | "personal";
    email_verified_at?: string | null;
    phone_verified_at?: string | null;
  } | null;
  user_data?: Json | null;
  business?: Json | null;
};

export type LoginPayload = {
  login: string; // email or username
  password: string;
  remember?: boolean;
};

export type RegisterPayloadCommon = {
  username: string;
  email: string;
  password: string;
  password_confirmation: string;
};

export type RegisterPersonalPayload = RegisterPayloadCommon & {
  type?: "personal"; // clarity only; server validates too
  customer_type?: "individual" | "personal";
  role?: string;
};

export type RegisterBusinessPayload = RegisterPayloadCommon & {
  type?: "business";
  role?: string;
};

export type RegisterDetailsType = "personal" | "business";

export type RegisterPersonalDetailsPayload = {
  type?: "personal";
  first_name: string;
  last_name: string;
  gender: "male" | "female" | "other";
  birthdate: string; // YYYY-MM-DD
  national_id_number: string;
  tax_id_number: string;
  phone_country_code: string; // e.g. +46
  phone_number: string;
  country: string; // country name (Rule::in list from config)
  city: string;
  address_line1: string;
  postal_code: string;
};

export type RegisterBusinessProfilePayload = {
  type?: "business";
  company_name: string;
  company_number: string;
  vat_number: string;
  address: string;
  postal_code: string;
  city: string;
  country: string; // country name (Rule::in list from config)
  phone_country_code: string; // REQUIRED by backend (422 without it)
  phone_number: string;       // REQUIRED by backend (422 without it)
};

export type PasswordForgotPayload = {
  email: string;
};

export type PasswordResetPayload = {
  email: string;
  token: string;
  password: string;
  password_confirmation: string;
};

/* =======================
 * Identity
 * ======================= */

export async function apiAuthMe(): Promise<MeResponse> {
  return request<MeResponse>("/xapi/auth/me", { method: "GET" });
}

/* =======================
 * Session
 * ======================= */

export async function apiLogin(
  payload: LoginPayload
): Promise<{ ok?: boolean; message?: string; user?: MeResponse["user"]; redirect?: string }> {
  return request("/xapi/auth/login", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function apiLogout(): Promise<{ ok: boolean }> {
  return request("/xapi/auth/logout", { method: "POST" });
}

/* =======================
 * Registration (Unified: PERSONAL)
 * These are already working in your app; keep as-is.
 * ======================= */

// Unified create
export async function apiRegister(
  payload: (RegisterPersonalPayload | RegisterBusinessPayload) & { type: RegisterDetailsType }
): Promise<{ message?: string; redirect?: string }> {
  return request("/xapi/auth/register", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Unified details
export async function apiRegisterDetails(
  payload:
    | (RegisterPersonalDetailsPayload & { type: "personal" })
    | (RegisterBusinessProfilePayload & { type: "business" })
): Promise<{ message?: string; redirect?: string }> {
  return request("/xapi/auth/register-details", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// Personal wrappers (use unified)
export async function apiRegisterPersonal(
  payload: RegisterPersonalPayload
): Promise<{ message?: string; redirect?: string }> {
  return apiRegister({
    ...payload,
    type: "personal",
    customer_type: payload.customer_type ?? "individual",
  });
}

export async function apiRegisterPersonalDetails(
  payload: RegisterPersonalDetailsPayload
): Promise<{ message?: string; redirect?: string }> {
  return apiRegisterDetails({ ...payload, type: "personal" });
}

/* =======================
 * Registration (BUSINESS — use dedicated endpoints)
 * Matches your working cURL and ensures redirect parity.
 * ======================= */

export async function apiRegisterBusiness(
  payload: RegisterBusinessPayload
): Promise<{ message?: string; redirect?: string }> {
  return request("/xapi/register/business", {
    method: "POST",
    body: JSON.stringify({
      ...payload,
      customer_type: "business",
    }),
  });
}

export async function apiRegisterBusinessProfile(
  payload: RegisterBusinessProfilePayload
): Promise<{ message?: string; redirect?: string }> {
  // This endpoint returns: { message: "...", redirect: "/app/dashboard" }
  return request("/xapi/register/business/profile", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

/* =======================
 * Email verification
 * ======================= */

export async function apiResendVerifyEmail(): Promise<{ message?: string }> {
  // routes/xapi.php → EmailVerificationController@resend at /auth/email/resend
  return request("/xapi/auth/email/resend", { method: "POST" });
}

/* =======================
 * Password flows
 * ======================= */

export async function apiPasswordForgot(
  payload: PasswordForgotPayload
): Promise<{ message?: string; status?: string }> {
  return request("/xapi/auth/password/forgot", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function apiPasswordReset(
  payload: PasswordResetPayload
): Promise<{ message?: string; status?: string }> {
  return request("/xapi/auth/password/reset", {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
